import { useEffect, useState } from 'react';

export function Hero() {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const rafId = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(rafId);
  }, []);

  return (
    <section className="relative flex min-h-screen items-center justify-center overflow-hidden">
      {/* Layered background effects */}
      <div className="bg-hero-gradient absolute inset-0" />

      {/* Ambient glow - top */}
      <div
        className={`bg-forest/[0.08] pointer-events-none absolute top-0 left-1/2 h-[800px] w-[1200px] -translate-x-1/2 rounded-full blur-[150px] transition-opacity duration-[2000ms] ${mounted ? 'opacity-100' : 'opacity-0'}`}
      />

      {/* Ambient glow - accent */}
      <div
        className={`bg-stream/[0.05] pointer-events-none absolute top-1/3 right-1/4 h-[400px] w-[400px] rounded-full blur-[120px] transition-opacity delay-500 duration-[2500ms] ${mounted ? 'opacity-100' : 'opacity-0'}`}
      />

      {/* Warm earth glow - bottom */}
      <div
        className={`bg-earth-sand/[0.04] pointer-events-none absolute bottom-0 left-1/3 h-[300px] w-[600px] rounded-full blur-[100px] transition-opacity delay-700 duration-[3000ms] ${mounted ? 'opacity-100' : 'opacity-0'}`}
      />

      {/* Decorative grid lines */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden opacity-[0.03]">
        <div className="absolute top-1/4 right-0 left-0 h-px bg-gradient-to-r from-transparent via-white to-transparent" />
        <div className="absolute top-3/4 right-0 left-0 h-px bg-gradient-to-r from-transparent via-white to-transparent" />
        <div className="absolute top-0 bottom-0 left-1/4 w-px bg-gradient-to-b from-transparent via-white to-transparent" />
        <div className="absolute top-0 right-1/4 bottom-0 w-px bg-gradient-to-b from-transparent via-white to-transparent" />
      </div>

      {/* Main content */}
      <div className="relative z-10 mx-auto max-w-6xl px-6 pt-32 pb-32 text-center">
        {/* Pre-heading label */}
        <div
          className={`mb-8 opacity-0 ${mounted ? 'animate-fade-in' : ''}`}
          style={{ animationDelay: '200ms', animationFillMode: 'forwards' }}
        >
          <span className="text-earth-sand/80 font-body inline-flex items-center gap-3 text-xs tracking-[0.4em] uppercase">
            <span className="to-earth-sand/50 h-px w-8 bg-gradient-to-r from-transparent" />
            Development Studio
            <span className="to-earth-sand/50 h-px w-8 bg-gradient-to-l from-transparent" />
          </span>
        </div>

        {/* Main headline - towering typography */}
        <h1
          className={`font-display mb-6 leading-[0.85] font-bold tracking-tight opacity-0 ${mounted ? 'animate-fade-in-up' : ''}`}
          style={{ animationDelay: '400ms', animationFillMode: 'forwards' }}
        >
          <span
            className="text-earth-cream block text-[clamp(4rem,18vw,14rem)] text-shadow-lg"
            style={{ letterSpacing: '-0.03em' }}
          >
            CORE
          </span>
          <span
            className="text-earth-cream -mt-4 block text-[clamp(4rem,18vw,14rem)] text-shadow-lg md:-mt-8"
            style={{ letterSpacing: '-0.03em' }}
          >
            ALPINE
          </span>
        </h1>

        {/* .dev suffix - glowing accent */}
        <div
          className={`relative mb-12 inline-block opacity-0 ${mounted ? 'animate-fade-in-up' : ''}`}
          style={{ animationDelay: '600ms', animationFillMode: 'forwards' }}
        >
          {/* Glow effect behind .dev */}
          <div className="from-forest via-stream to-forest absolute inset-0 scale-150 bg-gradient-to-r opacity-40 blur-2xl" />
          <span
            className="font-display text-gradient relative text-[clamp(2rem,8vw,6rem)] font-light"
            style={{ letterSpacing: '0.1em' }}
          >
            .dev
          </span>
        </div>

        {/* Tagline */}
        <p
          className={`font-body mx-auto mb-12 max-w-xl text-lg leading-relaxed font-light text-stone-400 opacity-0 md:text-xl ${mounted ? 'animate-fade-in-up' : ''}`}
          style={{ animationDelay: '800ms', animationFillMode: 'forwards' }}
        >
          Building the future with precision craftsmanship
          <br className="hidden md:block" />
          and alpine-inspired engineering.
        </p>

        {/* CTA area */}
        <div
          className={`flex flex-col items-center justify-center gap-4 opacity-0 sm:flex-row ${mounted ? 'animate-fade-in-up' : ''}`}
          style={{ animationDelay: '1000ms', animationFillMode: 'forwards' }}
        >
          <a href="#about" className="btn btn-primary group relative overflow-hidden">
            <span className="relative z-10">Explore</span>
            <div className="from-forest-light to-stream absolute inset-0 bg-gradient-to-r opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
          </a>

          <a
            href="https://corealpine.design"
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-ghost group"
          >
            <span>Visit</span>
            <span className="text-earth-sand">.design</span>
            <svg
              className="h-4 w-4 transform transition-transform group-hover:translate-x-1"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M17 8l4 4m0 0l-4 4m4-4H3"
              />
            </svg>
          </a>
        </div>
      </div>

      {/* Scroll indicator - hidden on short viewports */}
      <div
        className={`absolute bottom-12 left-1/2 hidden -translate-x-1/2 opacity-0 md:block [@media(max-height:1024px)]:hidden ${mounted ? 'animate-fade-in' : ''}`}
        style={{ animationDelay: '1400ms', animationFillMode: 'forwards' }}
      >
        <div className="animate-float flex flex-col items-center gap-2">
          <span className="font-body text-xs tracking-[0.3em] text-stone-600 uppercase">
            Scroll
          </span>
          <div className="h-8 w-px bg-gradient-to-b from-stone-600 to-transparent" />
        </div>
      </div>

      {/* Corner accents */}
      <div className="absolute top-8 left-8 h-16 w-16 border-t border-l border-stone-800/50 opacity-50" />
      <div className="absolute top-8 right-8 h-16 w-16 border-t border-r border-stone-800/50 opacity-50" />
      <div className="absolute bottom-8 left-8 h-16 w-16 border-b border-l border-stone-800/50 opacity-50" />
      <div className="absolute right-8 bottom-8 h-16 w-16 border-r border-b border-stone-800/50 opacity-50" />
    </section>
  );
}
